import React from 'react';
import { Drawer, Table } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { WarehouseLocationOrderType } from '../graphql/queries/getLocationOrderList';
import { useStatusInternationalization } from '../../../delivery/parcels/hooks/useStatusInternationalization';
import { GET_ALL_EXCEPTION, ExceptionDictionaryResponse, ExceptionDictionaryType } from '../../../delivery/parcels/graphql/queries/getAllException';
import { t } from '../../../../i18n';

interface LocationOrderDetailDrawerProps {
    visible: boolean;
    onClose: () => void;
    locationNo: string;
    orderList: WarehouseLocationOrderType[];
    loading: boolean;
}

const LocationOrderDetailDrawer: React.FC<LocationOrderDetailDrawerProps> = ({
    visible,
    onClose,
    locationNo,
    orderList,
    loading
}) => {
    const { getNextActionTextFromBackend } = useStatusInternationalization();
    const { i18n } = useTranslation();
    const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];

    const columns = [
        {
            title: t('wms.locationManagement.columns.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            align: 'left' as const,
        },
        {
            title: t('wms.locationManagement.columns.storageNo'),
            dataIndex: 'locationOrderNo',
            key: 'locationOrderNo',
            align: 'left' as const,
            render: (locationOrderNo: number | null) => locationOrderNo !== null && locationOrderNo !== undefined ? String(locationOrderNo).padStart(4, '0') : '-'
        },
        {
            title: t('wms.locationManagement.columns.nextAction'),
            dataIndex: 'nextAction',
            key: 'nextAction',
            align: 'left' as const,
            render: (action: string | null) => action ? getNextActionTextFromBackend(action) : '-'
        },
        {
            title: t('wms.locationManagement.columns.lastReturnReason'),
            dataIndex: 'exceptionReasonCode',
            key: 'exceptionReasonCode',
            align: 'left' as const,
            render: (code: string | null) => {
                if (!code) return '-';
                const exception = exceptionList.find(
                    (ex: ExceptionDictionaryType) => String(ex.exceptionReasonCode) === String(code)
                );
                if (!exception) return code;
                
                // 根据当前语言环境选择对应的文本，只显示文本不显示代码
                const currentLang = i18n.language || 'en';
                const normalizedLang = currentLang.startsWith('zh') ? 'zh' : 
                                      currentLang.startsWith('es') ? 'es' : 'en';
                
                if (normalizedLang === 'zh' && exception.secondaryTypeZh) {
                    return exception.secondaryTypeZh;
                } else if (normalizedLang === 'es' && exception.secondaryTypeEs) {
                    return exception.secondaryTypeEs;
                } else if (exception.secondaryTypeEn) {
                    return exception.secondaryTypeEn;
                }
                return code;
            }
        },
        {
            title: t('wms.locationManagement.columns.daysOnShelf'),
            dataIndex: 'putawayDays',
            key: 'putawayDays',
            align: 'left' as const,
            render: (days: number | null) => days !== null ? `${days} ${t('common.day')}` : '-'
        }
    ];

    return (
        <Drawer
            title={(
                <div className="w-full text-left">
                    <div className="flex items-center">
                        <span
                            className="mr-3"
                            style={{
                                width: 4,
                                height: 26,
                                backgroundColor: 'var(--ant-color-primary)',
                                borderRadius: 2
                            }}
                        />
                        <span>{t('wms.locationManagement.drawer.locationNo', { locationNo })}</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-2 pl-4">
                        {t('wms.locationManagement.drawer.shelvedOrders', { count: orderList.length })}
                    </div>
                </div>
            )}
            placement="right"
            width={1000}
            onClose={onClose}
            open={visible}
            destroyOnClose
            closeIcon={null}
        >
            <Table
                columns={columns}
                dataSource={orderList}
                rowKey="id"
                loading={loading}
                pagination={false}
                size="small"
            />
        </Drawer>
    );
};

export default LocationOrderDetailDrawer;

